<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { socialShareImage, usePageHead } from '@composables/use-page-head'
import { useQueueStore } from '@stores/queue'
import { useRoute } from 'vue-router'
import { useAppearanceStore } from '@stores/appearance'
import { useScrollContainer } from '@composables/use-scroll-container'
import { useAuthStore } from '@stores/auth'
import { useQueueService } from '@composables/use-queue-service'
import PlaylistsTableBlock from '@components/playlists/playlists-table-block'
import PlaylistInfo from '@components/playlists/playlist-info'
import DataLayer from '@services/data-layer/index'
import modalService from '@/services/modal-service'

const PAGE_SIZE = 20
const PLACEHOLDER_ROWS = 20
const LOAD_MORE_THROTTLE_MS = 200
const BOTTOM_OFFSET_RATIO = 3

const route = useRoute()
const appearanceStore = useAppearanceStore()
const scrollContainer = useScrollContainer()
const authStore = useAuthStore()
const queueStore = useQueueStore()
const queueService = useQueueService()

const playlist = ref(null)

const playlistMixes = ref(
    Array.from(Array(PLACEHOLDER_ROWS), (_mix, i) => ({
        id: Number.MAX_SAFE_INTEGER - i,
        genre: [],
    }))
)
const shouldTransition = ref(false)
const maxMixes = ref(PAGE_SIZE)
const totalMixes = ref(null)
const shouldLoadMore = ref(false)
const loaded = ref(false)

const id = computed(() => route.params.id)

const scroll = computed(() => appearanceStore.scroll)

const loggedIn = computed(() => authStore.loggedIn)

const currentPosition = computed(() => queueStore.position)

const hasReachedMaxMixes = computed(() => maxMixes.value >= Number(totalMixes.value))

function loadMoreMixes(fillAll = false) {
    loaded.value = false
    maxMixes.value += PAGE_SIZE
    const wanted = fillAll ? totalMixes.value - playlistMixes.value.length : PAGE_SIZE

    return DataLayer.playlists
        .getRevisionsMixes(id.value, wanted, playlistMixes.value.length)
        .then(mixes => {
            loaded.value = true
            return playlistMixes.value.push(...mixes.body.map(revision => revision.mixes))
        })
}

function playPlaylist(position) {
    if (!loggedIn.value) {
        modalService.show('AUTH_REQUIRED_FOR_PLAYBACK')
        return
    }

    const play = () =>
        queueService.playMixMultiple({
            mixes: [...playlistMixes.value],
            id: playlist.value.id,
            position,
        })

    // Playing the playlist queues all of it, so anything not paged in yet is fetched first.
    if (playlistMixes.value.length === totalMixes.value) {
        play()
    } else {
        loadMoreMixes(true).then(play)
    }
}

watch(scroll, () => {
    if (hasReachedMaxMixes.value) return

    shouldLoadMore.value = true

    const bottomOfWindow =
        scroll.value + window.innerHeight >=
        scrollContainer.value.offsetHeight - window.innerHeight / BOTTOM_OFFSET_RATIO

    if (bottomOfWindow && shouldLoadMore.value && loaded.value) {
        shouldLoadMore.value = false
        setTimeout(() => {
            shouldLoadMore.value = true
        }, LOAD_MORE_THROTTLE_MS)
        loadMoreMixes()
    }
})

onMounted(() => {
    DataLayer.playlists.getById(id.value).then(res => {
        playlist.value = res.body
    })

    DataLayer.playlists.getRevisionsMixes(id.value, PAGE_SIZE, 0).then(res => {
        loaded.value = true
        totalMixes.value = Number(res.headers['x-items-total'])
        playlistMixes.value = res.body.map(revision => revision.mixes)
    })
})

usePageHead(computed(() => ({
    title: playlist.value ? `${playlist.value.revisions_title} - Playlist` : 'Playlist',
    description: 'Stream curated DJ mixes and underground electronic music — house, techno, progressive, and more.',
    image: playlist.value?.revisions_album_art?.url || socialShareImage(),
    type: 'music.playlist',
})))
</script>

<template>
    <BaseContent>
        <div class="container">
            <BaseSection padded>
                <h6>Playlist</h6>
                <div class="content">
                    <div class="details">
                        <PlaylistInfo
                            :playlist-mixes="playlistMixes"
                            :playlist="playlist"
                            @play-playlist="playPlaylist(currentPosition !== -1 ? currentPosition : 0)"
                        />
                    </div>
                    <PlaylistsTableBlock
                        :mixes="playlistMixes"
                        :error="{
                            title: `There are no mixes in this playlist yet`,
                        }"
                        :should-transition="shouldTransition"
                        playlist-id
                        genres
                        @play-playlist-mix="playPlaylist"
                    />
                </div>
            </BaseSection>
        </div>
    </BaseContent>
</template>

<style lang="scss" scoped>
@import '@design';

.container {
    margin-top: $s-h;
}

.content {
    @include desktop {
        display: grid;
        grid-template-columns: 1fr 3fr;
        gap: $s-xxxl;
    }
}

.details {
    max-width: 300px;
    margin: 0 auto;
}
</style>
